import EquationInput from "../../../../components/inputs/EquationInput";
import IntervalInput,{parseIntervalInput,intervalToLatex} from "../../../../components/inputs/IntervalInput";
import {answerText,checkQuadraticEquation} from "../../../../utils/quadraticEquationAnswer";
import TaskAssessmentPanel from "../../../../components/TaskAssessmentPanel";
// src/generators/dzial2_algebra/temat5_zadania_uzupelniajace_maturalne/podtemat3_maturalne_otwarte_types/MaturaRationalIntervalOpenTask.js

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview as formatFracPreview,
} from "../../../../components/inputs/FractionInput";
import {
  parseRootFractionInput,
  formatLatexPreview as formatNumLatex,
} from "../../../../components/inputs/RootFractionInput";

// bazujemy na generatorze z równań wymiernych (2 frakcje → kwadratowe)
import {
  generateTask as generateRationalQuadTask,
} from "../../../../generators/dzial2_algebra/temat2_rownania/podtemat3_wymierne_types/RationalTwoFracsQuadraticTask";

/* ===== helpers ===== */

const tol = 0.01 + 1e-9;
const eq = (x, y) => Math.abs(x - y) <= tol;

function normalizeUserNumberText(txt) {
  return String(txt ?? "")
    .replace(/[−–—]/g, "-")
    .replace(/\u00A0|\u202F|\u2009|\u200A|\u200B/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parseNumberFromAny(v) {
  if (v == null) return NaN;

  if (typeof v === "object") {
    if (Number.isFinite(v.parsed)) return v.parsed;
    if (typeof v.raw === "string") {
      const s = normalizeUserNumberText(v.raw).replace(",", ".");
      return parseRootFractionInput(s);
    }
  }

  if (typeof v === "number") return v;
  if (typeof v === "string") {
    const s = normalizeUserNumberText(v).replace(",", ".");
    return parseRootFractionInput(s);
  }

  return NaN;
}

function niceLatex(val) {
  return formatNumLatex(String(val), "") || String(val);
}

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  while (b) {
    const t = a % b;
    a = b;
    b = t;
  }
  return a || 1;
};

function simplifyFrac(num, den) {
  if (den < 0) {
    num = -num;
    den = -den;
  }
  const g = gcd(num, den);
  return { num: num / g, den: den / g };
}

// ułamek z minusem wyciągniętym przed całość
function fracLatex(num, den) {
  const { num: N0, den: D } = simplifyFrac(num, den);

  if (D === 1) return String(N0);
  if (N0 === 0) return "0";

  const sign = N0 < 0 ? "-" : "";
  const N = Math.abs(N0);

  return `${sign}\\dfrac{${N}}{${D}}`;
}

/**
 * Znajduje „ładny” ułamek między dwoma liczbami rzeczywistymi
 * (z małym mianownikiem 2..9). Zwraca { left_num, left_den, left_val } lub null.
 */
function findNiceLeftBetween(low, high) {
  if (!(low < high)) return null;

  for (let den = 2; den <= 9; den += 1) {
    const minN = Math.floor(low * den + 1e-9) + 1; // najmniejsze n z n/den > low
    const maxN = Math.floor(high * den - 1e-9); // największe n z n/den < high
    if (minN <= maxN) {
      const num = minN;
      const { num: N, den: D } = simplifyFrac(num, den);
      return { left_num: N, left_den: D, left_val: N / D };
    }
  }
  return null;
}

/* ===== generator ===== */

/**
 * Wariant „oryginał” – matura podstawowa, sierpień 2025, zadanie 8:
 *   3/(3x - 7) = 5x/(x - 8),  x ≠ 7/3, x ≠ 8.
 *   Po przekształceniu: 15x^2 - 38x + 24 = 0,  Δ = 4,  x = 6/5 lub x = 4/3.
 *   W przedziale (5/4, +∞) leży tylko x = 4/3.
 * Podajemy tylko pola realnie używane przez render/check/explanation.
 */
function createOriginalTask() {
  return {
    type: "MaturaRationalIntervalOpenTask",
    data: {
      latex: "\\dfrac{3}{3x - 7} = \\dfrac{5x}{x - 8}",
      A: 15,
      B: -38,
      C: 24,
      // pierwiastki: mniejszy / większy
      x1_val: 6 / 5,
      x2_val: 4 / 3,
      x1_num: 6,
      x1_den: 5,
      x2_num: 4,
      x2_den: 3,
      // wykluczenia z dziedziny: 7/3 oraz 8
      D1_num: 7,
      D1_den: 3,
      D2_num: 8,
      D2_den: 1,
      // lewy kraniec przedziału (5/4, +∞)
      left_num: 5,
      left_den: 4,
      left_val: 5 / 4,
      maxPoints: 3,
    },
    source: {
      variant: "original",
      exam: "MATURA",
      session: "2025_SIE",
      taskNumber: 8,
    },
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  // generujemy równanie wymierne jak w temacie równań (dwie frakcje → kwadratowe)
  // i na jego podstawie budujemy wersję „maturalną” z przedziałem (a, +∞),
  // w którym leży tylko większy pierwiastek.
  while (true) {
    const base = generateRationalQuadTask();
    const d = base.data;

    const {
      latex,
      A2,
      B2,
      C2,
      x0_num,
      x0_den,
      x0_val,
      x1_num,
      x1_den,
      x1_val,
      r1_num,
      r1_den,
      r1_val,
      r2_num,
      r2_den,
      r2_val,
      // współczynniki homografii po lewej i prawej stronie:
      // lewa: (a x + b) / (c x + dCoef)
      // prawa: (e x + f) / (g x + h)
      a,
      b,
      c,
      d: dCoef,
      e,
      f,
      g,
      h,
    } = d;

    // odrzucamy przypadki, gdy którakolwiek strona jest stałą liczbą
    // (a*d - b*c = 0 albo e*h - f*g = 0), ale tylko jeśli mamy pełne dane
    const leftHasAll = [a, b, c, dCoef].every((v) => Number.isFinite(v));
    const rightHasAll = [e, f, g, h].every((v) => Number.isFinite(v));
    const leftIsConst = leftHasAll && a * dCoef === b * c;
    const rightIsConst = rightHasAll && e * h === f * g;
    if (leftIsConst || rightIsConst) {
      continue;
    }

    if (
      !Number.isFinite(r1_val) ||
      !Number.isFinite(r2_val) ||
      r1_val === r2_val
    ) {
      continue;
    }

    // chcemy „normalny” przypadek: dwa dodatnie pierwiastki
    const smallVal = Math.min(r1_val, r2_val);
    const bigVal = Math.max(r1_val, r2_val);
    if (!(smallVal > 0)) continue;

    // lewy kraniec przedziału (a, +∞): między pierwiastkami
    const left = findNiceLeftBetween(smallVal, bigVal);
    if (!left) continue;

    const smallIsR1 = r1_val < r2_val;
    const smallNum = smallIsR1 ? r1_num : r2_num;
    const smallDen = smallIsR1 ? r1_den : r2_den;
    const bigNum = smallIsR1 ? r2_num : r1_num;
    const bigDen = smallIsR1 ? r2_den : r1_den;

    const xSmallVal = smallVal;
    const xBigVal = bigVal;

    const A = A2;
    const B = B2;
    const C = C2;

    const maxPoints = 3;

    return {
      type: "MaturaRationalIntervalOpenTask",
      data: {
        latex,
        // współczynniki kwadratowego
        A,
        B,
        C,
        // pierwiastki równania (mały / duży)
        x1_val: xSmallVal,
        x2_val: xBigVal,
        x1_num: smallNum,
        x1_den: smallDen,
        x2_num: bigNum,
        x2_den: bigDen,
        // dziedzina – miejsca wykluczone z mianowników
        x0_num,
        x0_den,
        x0_val,
        x1_num_den: x1_num,
        x1_den_den: x1_den,
        x_den_num: x1_num,
        x_den_den: x1_den,
        x_forbidden2_num: x1_num,
        x_forbidden2_den: x1_den,
        // faktyczne wykluczenia (przepiszmy czytelnie)
        D1_num: x0_num,
        D1_den: x0_den,
        D1_val: x0_val,
        D2_num: x1_num,
        D2_den: x1_den,
        D2_val: x1_val,
        // lewy kraniec przedziału
        left_num: left.left_num,
        left_den: left.left_den,
        left_val: left.left_val,
        maxPoints,
      },
      source: { variant: "random", basedOn: "MATURA_2025_SIE_8" },
    };
  }
}

/* ===== treść zadania ===== */

export function renderTask({ data }) {
  const {
    latex,
    left_num,
    left_den,
    D1_num,
    D1_den,
    D2_num,
    D2_den,
  } = data;

  const intervalLatex = data.intervalLatex || `\\left(${fracLatex(left_num, left_den)},\\; +\\infty\\right)`;
  const d1Latex = fracLatex(D1_num, D1_den);
  const d2Latex = fracLatex(D2_num, D2_den);

  return (
    <div>
      <p>Dane jest równanie</p>
      <p style={{ marginTop: 4, marginBottom: 4 }}>
        <InlineMath math={latex} /> , gdzie{" "}
        <InlineMath math={`x \\ne ${d1Latex}`} /> oraz{" "}
        <InlineMath math={`x \\ne ${d2Latex}`} />.
      </p>
      <p style={{ marginTop: 8 }}>
        Wyznacz wszystkie rozwiązania tego równania należące do przedziału{" "}
        <InlineMath math={intervalLatex} />.
      </p>
      <p style={{ marginTop: 8 }}>
        Zapisz obliczenia. Możesz skorzystać z poniższych kroków.
      </p>
    </div>
  );
}

/* ===== input użytkownika (3 kroki) ===== */

export function renderInput(task,userInput={},onChange,disabled,renderTaskPreview){
 const {left_num,left_den}=task.data;
 const intervalLatex=task.data.intervalLatex || String.raw`\left(${fracLatex(left_num,left_den)}; +\infty\right)`;
 const set=(key,value)=>onChange({...userInput,[key]:value});
 return <div style={{display:"grid",gap:18}}>
  <div><p>Równanie kwadratowe po usunięciu mianowników</p>
   <EquationInput value={answerText(userInput.equation)} onChange={v=>set("equation",v)} disabled={disabled} placeholder="ax^2+bx+c=0 lub równoważne równanie"/>
  </div>
  <div><p>Zbiór rozwiązań otrzymanego równania</p>
   <IntervalInput value={answerText(userInput.roots)} onChange={v=>set("roots",v)} disabled={disabled} label="" placeholder="np. {-2; 1/3} lub {2,5; 3,5}"/>
  </div>
  <div><p>Rozwiązanie należące do przedziału <InlineMath math={intervalLatex}/></p>
   <FractionInput value={userInput.xf || ""} onChange={v=>set("xf",v)} disabled={disabled} label="x"/>
  </div>
  <TaskAssessmentPanel task={task} answer={userInput} onChange={onChange} disabled={disabled} renderTaskPreview={renderTaskPreview || (()=>renderTask(task))}/>
 </div>;
}

export function checkAnswer({ data }, userInput = {}) {
  const { A, B, C, x1_val, x2_val, maxPoints } = data;

  const a = parseNumberFromAny(userInput.a);
  const b = parseNumberFromAny(userInput.b);
  const c = parseNumberFromAny(userInput.c);

  const gotX1 = parseNumberFromAny(userInput.x1);
  const gotX2 = parseNumberFromAny(userInput.x2);
  const gotXf = parseNumberFromAny(userInput.xf);

  // współczynniki – akceptujemy wersję pomnożoną przez stałą k ≠ 0
  let coeffsOK = false;

  if (Number.isFinite(a) && Number.isFinite(b) && Number.isFinite(c)) {
    // wybieramy referencyjny współczynnik, który nie jest 0
    const ref =
      Math.abs(A) > tol
        ? { u: a, v: A }
        : Math.abs(B) > tol
        ? { u: b, v: B }
        : Math.abs(C) > tol
        ? { u: c, v: C }
        : null;

    if (ref && Math.abs(ref.v) > tol) {
      const k = ref.u / ref.v;
      if (Number.isFinite(k) && Math.abs(k) > tol) {
        coeffsOK =
          eq(a, k * A) &&
          eq(b, k * B) &&
          eq(c, k * C);
      }
    }
  }

  const equationCheck=checkQuadraticEquation(userInput.equation,[A,B,C]);
  if(Object.prototype.hasOwnProperty.call(userInput,"equation"))coeffsOK=equationCheck.correct;

  const roots = [];
  if (Number.isFinite(gotX1)) roots.push(gotX1);
  if (Number.isFinite(gotX2)) roots.push(gotX2);
  const expRoots = [x1_val, x2_val].sort((u, v) => u - v);
  const parsedSet=parseIntervalInput(answerText(userInput.roots));
  const gotRoots = (Object.prototype.hasOwnProperty.call(userInput,"roots") ? (parsedSet?.kind==="finite" ? parsedSet.points : []) : roots).slice().sort((u, v) => u - v);

  const rootsOK =
    gotRoots.length === 2 &&
    eq(gotRoots[0], expRoots[0]) &&
    eq(gotRoots[1], expRoots[1]);

  // poprawne rozwiązanie w przedziale – zawsze większy pierwiastek
  const inIntervalOK = Number.isFinite(gotXf) && eq(gotXf, data.selectedRoot ?? x2_val);

  const parts = {
    "równanie kwadratowe": coeffsOK,
    "pierwiastki równania": rootsOK,
    "rozwiązanie w przedziale": inIntervalOK,
  };

  // Progi CKE są hierarchiczne:
  // 1 pkt - poprawne równanie kwadratowe,
  // 2 pkt - rozwiązanie równania w R,
  // 3 pkt - wskazanie rozwiązania należącego do zadanego przedziału.
  let score = 0;
  if (rootsOK && inIntervalOK) {
    score = 3;
  } else if (rootsOK) {
    score = 2;
  } else if (coeffsOK) {
    score = 1;
  }

  return {
    message: parsedSet?.kind === "ambiguous" ? "Nie można jednoznacznie odczytać zbioru. Oddziel rozwiązania średnikiem, np. {2,5; 3,5}." : equationCheck.correct ? equationCheck.messages.join(" ") : undefined,
    isCorrect: score === maxPoints,
    score,
    maxScore: maxPoints,
    parts,
  };
}

/* ===== krótka poprawna odpowiedź ===== */

export function renderAnswer({ data }) {
  const {
    A,
    B,
    C,
    x1_num,
    x1_den,
    x2_num,
    x2_den,
    left_num,
    left_den,
  } = data;

  const quadLatex = `${A}x^2 ${
    B >= 0 ? "+ " + B : "- " + Math.abs(B)
  }x ${C >= 0 ? "+ " + C : "- " + Math.abs(C)} = 0`;

  const x1L = fracLatex(x1_num, x1_den);
  const x2L = fracLatex(x2_num, x2_den);
  const leftL = fracLatex(left_num, left_den);

  return (
    <div
      style={{
        textAlign: "left",
        marginTop: "0.5rem",
        lineHeight: 1.5,
      }}
    >
      <div>
        <InlineMath math={quadLatex} />
      </div>
  <div style={{ marginTop: 10 }}>
    <InlineMath math={`x_1 = ${x1L},\\; x_2 = ${x2L}`} />
  </div>
  <div style={{ marginTop: 10 }}>
    <InlineMath math={`x\\;=\\;${x2L}\\;\\in\\;\\left(${leftL},\\; +\\infty\\right)`} />
  </div>
    </div>
  );
}

/* ===== podgląd odpowiedzi użytkownika ===== */

export function renderUserAnswer(userInput = {}) {
  if(Object.prototype.hasOwnProperty.call(userInput,"equation")||Object.prototype.hasOwnProperty.call(userInput,"roots"))return <div>
    <p>Równanie: <InlineMath math={formatFracPreview(answerText(userInput.equation),"") || "\\text{brak}"}/></p>
    <p>Rozwiązania równania: {parseIntervalInput(answerText(userInput.roots)) ? <InlineMath math={intervalToLatex(answerText(userInput.roots))}/> : answerText(userInput.roots) || "brak"}</p>
    <p>Wybrane rozwiązanie: <InlineMath math={formatFracPreview(answerText(userInput.xf),"") || "\\text{brak}"}/></p>
  </div>;

  const getRaw = (v) =>
    typeof v === "object" ? String(v?.raw ?? "").trim() : String(v ?? "").trim();

  const aRaw = getRaw(userInput.a);
  const bRaw = getRaw(userInput.b);
  const cRaw = getRaw(userInput.c);
  const x1Raw = getRaw(userInput.x1);
  const x2Raw = getRaw(userInput.x2);
  const xfRaw = getRaw(userInput.xf);

  const allEmpty = !aRaw && !bRaw && !cRaw && !x1Raw && !x2Raw && !xfRaw;
  if (allEmpty) return <i>(brak odpowiedzi)</i>;

  const one = (labelLatex, v) => {
    const raw = typeof v === "object" ? (v?.raw ?? "") : String(v ?? "");
    const trimmed = raw.trim();

    if (!trimmed) {
      return (
        <div>
          <InlineMath math={`${labelLatex}\\;=\\;\\text{–}`} />
        </div>
      );
    }

    const latex = formatFracPreview(trimmed, "");
    return (
      <div>
        <InlineMath math={`${labelLatex}\\;=\\;${latex.replace(/^=/, "")}`} />
      </div>
    );
  };

  const items = [
    one("a", userInput.a),
    one("b", userInput.b),
    one("c", userInput.c),
    one("x_1", userInput.x1),
    one("x_2", userInput.x2),
    one("x", userInput.xf),
  ];

  return (
    <div style={{ lineHeight: 1.8 }}>
      {items.map((it, idx) => (
        <div key={idx} style={{ marginTop: idx === 0 ? 0 : 10 }}>
          {it}
        </div>
      ))}
    </div>
  );
}

/* ===== wyjaśnienie krok po kroku ===== */

export function renderExplanation({ data }) {
  const {
    D1_num, D1_den, D2_num, D2_den,
    A,
    B,
    C,
    x1_num,
    x1_den,
    x2_num,
    x2_den,
    left_num,
    left_den,
    latex,
  } = data;

  const quadLatex = `${A}x^2 ${
    B >= 0 ? "+ " + B : "- " + Math.abs(B)
  }x ${C >= 0 ? "+ " + C : "- " + Math.abs(C)} = 0`;

  const x1L = fracLatex(x1_num, x1_den);
  const x2L = fracLatex(x2_num, x2_den);
  const leftL = fracLatex(left_num, left_den);

  const delta = B * B - 4 * A * C;
  const sqrtDelta = Math.sqrt(Math.abs(delta));
  const bInParens = B >= 0 ? `${B}` : `(${B})`;

  const deltaLatex = `\\Delta = (${bInParens})^2 - 4\\cdot ${A}\\cdot ${C} = ${delta}`;

  return (
    <div className="space-y-3" style={{ textAlign: "left" }}>
      <p><strong>Krok 1.</strong> Zapisujemy wykluczenia z pierwotnych mianowników.</p>
      <BlockMath math={`x\\ne${fracLatex(D1_num,D1_den)},\\quad x\\ne${fracLatex(D2_num,D2_den)}`} />
      {/* KROK 1 */}
      <p>
        <strong>Krok 2.</strong> Mnożymy obie strony równania przez iloczyn
        mianowników i sprowadzamy je do wspólnego mianownika.
      </p>

      <BlockMath math={latex} />

      <p>Po uporządkowaniu wyrazów otrzymujemy równanie kwadratowe:</p>

      <BlockMath math={quadLatex} />

      {/* KROK 2 */}
      <p>
        <strong>Krok 3.</strong> Obliczamy deltę i pierwiastki równania
        kwadratowego.
      </p>

      <BlockMath math={deltaLatex} />
      <BlockMath
        math={`x_{1,2} = \\dfrac{-${bInParens} \\pm ${sqrtDelta}}{2\\cdot ${A}}`}
      />
      <BlockMath math={`x_1 = ${x1L},\\quad x_2 = ${x2L}`} />

      {/* KROK 3 */}
      <p>
        <strong>Krok 4.</strong> Sprawdzamy, które rozwiązania należą do
        przedziału{" "}
        <InlineMath math={`\\left(${leftL},\\; +\\infty\\right)`} />.
      </p>

      <BlockMath math={`x_1,x_2\\notin\\{${fracLatex(D1_num,D1_den)},${fracLatex(D2_num,D2_den)}\\},\\quad x_1 < ${leftL} < x_2`} />

      <p>
        Zatem jedynym rozwiązaniem spełniającym warunek zadania jest{" "}
        <InlineMath math={`x = ${x2L}`} />.
      </p>
    </div>
  );
}
